/**
 * Capital Markets unified fundings + funding tapes — wraps `/fundings/*`
 * (eastview-os frontend_documentation/closing-readiness.md §5 and
 * funding-tape-prep.md, 2026-09-14). Bearer auth via losHttp.
 *
 * One funding record per loan across BOTH LOS (`id = "<los>:<dealId>"`) in the
 * shared capital_markets DB. A tape is a named column set with a source ladder
 * per column; rows carry the populated values with field-level provenance and,
 * for document-sourced columns, an `agreement` (LOS value vs document read).
 */
import { losHttp } from "../lib/losHttp";

const cfg = (logName) => ({ logName });

const clean = (params = {}) =>
  Object.fromEntries(
    Object.entries(params).filter(
      ([, v]) => v !== undefined && v !== null && v !== "",
    ),
  );

export const TAPE_STATUSES = ["open", "locked", "funded", "archived"];

// Reviewer resolutions for a LOS-vs-document disagreement (§3.2).
export const RESOLUTIONS = {
  LOS: "los", // keep the LOS value
  DOCUMENT: "document", // adopt the document value (does NOT write the loan)
  MANUAL: "manual", // + `value`
};

export const fundingsApi = {
  // GET /fundings?start&end&los → funding records in a window.
  list: ({ start, end, los } = {}) =>
    losHttp
      .get("/fundings", { params: clean({ start, end, los }), ...cfg("Fundings") })
      .then((r) => r.data),

  // GET /fundings/forward?days=5[&los=all|this|<portal>] → the forward view
  // with pull-through: { byDay[{date, count, amount, expectedAmount,
  // readyCount, lockExpiring, items[]}] }.
  forward: ({ days = 5, los } = {}) =>
    losHttp
      .get("/fundings/forward", {
        params: clean({ days, los }),
        ...cfg("Fundings forward view"),
      })
      .then((r) => r.data),

  // POST /fundings/sync?start&end[&pipeline] → rebuild the auto-scope records
  // for a window (idempotent).
  sync: ({ start, end, pipeline } = {}) =>
    losHttp
      .post("/fundings/sync", {}, { params: clean({ start, end, pipeline }), ...cfg("Sync fundings") })
      .then((r) => r.data),

  // GET /fundings/record/{dealId} → the loan's funding record (activeTapeIds,
  // projection accuracy, moves, tapes{...}).
  getRecord: (dealId) =>
    losHttp
      .get(`/fundings/record/${dealId}`, cfg("Funding record"))
      .then((r) => r.data),

  // POST /fundings/record/{dealId}/sync → re-sync one record from the loan.
  syncRecord: (dealId) =>
    losHttp
      .post(`/fundings/record/${dealId}/sync`, {}, cfg("Sync funding record"))
      .then((r) => r.data),

  // GET /fundings/tapes → { tapes[] } (specs). Defaults are seeded on first
  // read: funding_tape, warehouse_draw, closed_loan_tape.
  getTapes: () =>
    losHttp.get("/fundings/tapes", cfg("Funding tapes")).then((r) => r.data),

  // POST /fundings/tapes {spec} → create / edit a tape spec (a spec edit never
  // drops the loan lists).
  createTape: (spec) =>
    losHttp
      .post("/fundings/tapes", spec, cfg("Create funding tape"))
      .then((r) => r.data),

  // GET /fundings/tapes/{tapeId}/rows[?include_exited=true] → { tape, count,
  // rows[{ values{field: {value, source, required, missing, agreement}},
  // missingRequired[], pullThroughProbability, projection{}, diffs[] }] }.
  getRows: (tapeId, { includeExited = false } = {}) =>
    losHttp
      .get(`/fundings/tapes/${tapeId}/rows`, {
        params: clean({ include_exited: includeExited ? "true" : undefined }),
        ...cfg("Funding tape rows"),
      })
      .then((r) => r.data),

  // GET /fundings/tapes/{tapeId}/xlsx → the Excel pull as a Blob (sheets Tape /
  // Provenance / Diffs). The caller turns it into a download.
  getXlsx: (tapeId) =>
    losHttp
      .get(`/fundings/tapes/${tapeId}/xlsx`, {
        responseType: "blob",
        ...cfg("Funding tape xlsx"),
      })
      .then((r) => r.data),

  // POST /fundings/tapes/{tapeId}/loans { dealIds[] } → add loans (populated by
  // the tape's spec).
  addLoans: (tapeId, dealIds) =>
    losHttp
      .post(`/fundings/tapes/${tapeId}/loans`, { dealIds }, cfg("Add loans to tape"))
      .then((r) => r.data),

  // DELETE /fundings/tapes/{tapeId}/loans/{dealId} → remove.
  removeLoan: (tapeId, dealId) =>
    losHttp
      .delete(`/fundings/tapes/${tapeId}/loans/${dealId}`, cfg("Remove loan from tape"))
      .then((r) => r.data),

  // POST /fundings/tapes/{tapeId}/move/{dealId} { toTapeId } → move; the
  // target is populated by ITS required set, the diff is recorded.
  moveLoan: (tapeId, dealId, toTapeId) =>
    losHttp
      .post(`/fundings/tapes/${tapeId}/move/${dealId}`, { toTapeId }, cfg("Move loan between tapes"))
      .then((r) => r.data),

  // POST /fundings/tapes/{tapeId}/status { status, fundingDate? } — `funded`
  // ties every closed/settled loan on the tape and freezes projection accuracy.
  setStatus: (tapeId, { status, fundingDate } = {}) =>
    losHttp
      .post(
        `/fundings/tapes/${tapeId}/status`,
        clean({ status, fundingDate }),
        cfg("Set tape status"),
      )
      .then((r) => r.data),

  // GET /fundings/tapes/{tapeId}/reconcile/{dealId} → { tapeId, dealId,
  // summary{agree, disagree, los_only, no_source}, columns[] }. Puts the loan
  // on the tape if it isn't already. Never writes the loan.
  reconcile: (tapeId, dealId) =>
    losHttp
      .get(`/fundings/tapes/${tapeId}/reconcile/${dealId}`, cfg("Reconcile tape row"))
      .then((r) => r.data),

  // POST /fundings/tapes/{tapeId}/reconcile/{dealId}/resolve
  // { field, resolution: los|document|manual, value?, note? } → the column
  // with agreement.status = "resolved".
  resolve: (tapeId, dealId, { field, resolution, value, note } = {}) =>
    losHttp
      .post(
        `/fundings/tapes/${tapeId}/reconcile/${dealId}/resolve`,
        clean({ field, resolution, value, note }),
        cfg("Resolve tape disagreement"),
      )
      .then((r) => r.data),
};

export default fundingsApi;
